/**
 * Market Research renderer (artifact_name: `market_research`,
 * tool `tool_market_research`). Renders MarketResearchOutput's five components
 * (market landscape, competitive landscape, target-user reality, timing, and
 * recommendation) + sources. No `actions` on this artifact. Model = parsed JSON.
 */

import React from 'react';
import { CardShell } from './shared/components';
import {
  Divided,
  KV,
  Pill,
  Prose,
  Section,
  SourcesBlock,
  isRecord,
  makeNarrativeEditor,
  proseHtml,
  sectionHtml,
  sourcesHtml,
  str,
  strArr,
} from './shared/atlas';
import { htmlDoc } from './shared/html';
import type { ArtifactRenderer } from './types';

export type MarketResearchModel = Record<string, unknown>;

const arr = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter(isRecord) : [];
const rec = (v: unknown): Record<string, unknown> => (isRecord(v) ? v : {});

// -- parse / serialize -------------------------------------------------------

export function parseMarketResearch(json: Record<string, unknown>): MarketResearchModel | null {
  // Identified by its component blocks.
  if (!isRecord(json.market_landscape) && !isRecord(json.competitive_landscape)) return null;
  if (!isRecord(json.recommendation) && !isRecord(json.target_user_reality)) return null;
  return json;
}

const marketToJson = (m: MarketResearchModel): Record<string, unknown> => m;

// -- card --------------------------------------------------------------------

const EvidenceQuality: React.FC<{ value: unknown }> = ({ value }) =>
  str(value) ? <Pill value={str(value)} /> : null;

/** A titled list of MarketPlayer entries (name — description). */
const PlayerList: React.FC<{ label: string; players: unknown }> = ({ label, players }) => {
  const list = arr(players);
  if (list.length === 0) return null;
  return (
    <KV label={label}>
      <ul className="space-y-1.5">
        {list.map((p, i) => (
          <li key={i}>
            <span className="font-medium text-gray-900 dark:text-gray-100">{str(p.player_name)}</span>
            {str(p.description) && (
              <span className="text-gray-600 dark:text-gray-300"> — {str(p.description)}</span>
            )}
            {str(p.evidence_quality) && (
              <Pill value={str(p.evidence_quality)} className="ml-2 align-middle" />
            )}
          </li>
        ))}
      </ul>
    </KV>
  );
};

const MarketResearchCard: React.FC<{ model: MarketResearchModel }> = ({ model }) => {
  const market = rec(model.market_landscape);
  const comp = rec(model.competitive_landscape);
  const user = rec(model.target_user_reality);
  const timing = rec(model.timing_momentum);
  const rec_ = rec(model.recommendation);
  const whiteSpace = arr(comp.white_space);

  return (
    <CardShell testId="market-research-artifact-card">
      <Section title="Market landscape" trailing={<EvidenceQuality value={market.evidence_quality} />}>
        <Divided>
          {str(market.market_definition) && <KV label="Definition"><Prose>{str(market.market_definition)}</Prose></KV>}
          {str(market.market_size_signals) && <KV label="Size signals"><Prose>{str(market.market_size_signals)}</Prose></KV>}
          {str(market.growth_drivers) && <KV label="Growth drivers"><Prose>{str(market.growth_drivers)}</Prose></KV>}
          {str(market.market_maturity) && (
            <KV label="Maturity"><Pill value={str(market.market_maturity)} /></KV>
          )}
        </Divided>
      </Section>

      <Section title="Competitive landscape" trailing={<EvidenceQuality value={comp.evidence_quality} />}>
        <Divided>
          <PlayerList label="Direct" players={comp.direct_competitors} />
          <PlayerList label="Indirect" players={comp.indirect_competitors} />
          <PlayerList label="Adjacent" players={comp.adjacent_solutions} />
          <PlayerList label="Non-consumption" players={comp.non_consumption} />
          {str(comp.pricing_map) && <KV label="Pricing map"><Prose>{str(comp.pricing_map)}</Prose></KV>}
          {whiteSpace.length > 0 && (
            <KV label="White space">
              <ul className="space-y-1.5">
                {whiteSpace.map((w, i) => (
                  <li key={i}>
                    {str(w.gap_description)}
                    {str(w.evidence_quality) && <Pill value={str(w.evidence_quality)} className="ml-2 align-middle" />}
                  </li>
                ))}
              </ul>
            </KV>
          )}
        </Divided>
      </Section>

      <Section title="Target user reality" trailing={<EvidenceQuality value={user.evidence_quality} />}>
        <Divided>
          {str(user.stated_icp_vs_observed) && <KV label="ICP vs observed"><Prose>{str(user.stated_icp_vs_observed)}</Prose></KV>}
          {str(user.buyer_behavior) && <KV label="Buyer behavior"><Prose>{str(user.buyer_behavior)}</Prose></KV>}
          {str(user.willingness_to_pay) && <KV label="Willingness to pay"><Prose>{str(user.willingness_to_pay)}</Prose></KV>}
          {str(user.switching_cost) && <KV label="Switching cost"><Prose>{str(user.switching_cost)}</Prose></KV>}
        </Divided>
      </Section>

      <Section title="Timing & momentum" trailing={<EvidenceQuality value={timing.evidence_quality} />}>
        <Divided>
          {str(timing.window_read) && <KV label="Window"><Pill value={str(timing.window_read)} /></KV>}
          {strArr(timing.timing_signals).length > 0 && (
            <KV label="Signals">
              <ul className="list-disc space-y-1 pl-4">
                {strArr(timing.timing_signals).map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </KV>
          )}
        </Divided>
      </Section>

      <Section title="Recommendation" trailing={<EvidenceQuality value={rec_.evidence_quality} />}>
        <Divided>
          {str(rec_.verdict_fit) && <KV label="Verdict / fit"><Prose>{str(rec_.verdict_fit)}</Prose></KV>}
          {strArr(rec_.assumptions_exposed).length > 0 && (
            <KV label="Assumptions">
              <ul className="list-disc space-y-1 pl-4">
                {strArr(rec_.assumptions_exposed).map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </KV>
          )}
          {str(rec_.what_to_validate_next) && <KV label="Validate next"><Prose>{str(rec_.what_to_validate_next)}</Prose></KV>}
        </Divided>
      </Section>

      <SourcesBlock sources={model.sources} />
    </CardShell>
  );
};

// -- editor ------------------------------------------------------------------

const MarketResearchEditor = makeNarrativeEditor<MarketResearchModel>(
  [
    {
      label: 'Recommendation — verdict / fit',
      rows: 3,
      get: (m) => str(rec(m.recommendation).verdict_fit),
      set: (m, v) => {
        if (!isRecord(m.recommendation)) m.recommendation = {};
        (m.recommendation as Record<string, unknown>).verdict_fit = v;
      },
    },
    {
      label: 'What to validate next',
      rows: 3,
      get: (m) => str(rec(m.recommendation).what_to_validate_next),
      set: (m, v) => {
        if (!isRecord(m.recommendation)) m.recommendation = {};
        (m.recommendation as Record<string, unknown>).what_to_validate_next = v;
      },
    },
  ],
  marketToJson
);

// -- markdown / html ---------------------------------------------------------

export function marketResearchToMarkdown(model: MarketResearchModel): string {
  const market = rec(model.market_landscape);
  const rec_ = rec(model.recommendation);
  const lines: string[] = ['# Market Research'];
  if (str(market.market_definition)) lines.push(`\n## Market landscape\n\n${str(market.market_definition)}`);
  if (str(rec_.verdict_fit)) lines.push(`\n## Recommendation\n\n${str(rec_.verdict_fit)}`);
  if (str(rec_.what_to_validate_next)) lines.push(`\n**Validate next:** ${str(rec_.what_to_validate_next)}`);
  return lines.join('\n');
}

export function marketResearchToHtml(model: MarketResearchModel, title = 'Market Research'): string {
  const market = rec(model.market_landscape);
  const user = rec(model.target_user_reality);
  const rec_ = rec(model.recommendation);
  const body = [
    sectionHtml('Market landscape', proseHtml(str(market.market_definition)) + proseHtml(str(market.growth_drivers))),
    sectionHtml('Target user reality', proseHtml(str(user.stated_icp_vs_observed)) + proseHtml(str(user.buyer_behavior))),
    sectionHtml('Recommendation', proseHtml(str(rec_.verdict_fit)) + proseHtml(str(rec_.what_to_validate_next))),
    sourcesHtml(model.sources),
  ].join('');
  return htmlDoc(title, body);
}

export const marketResearchRenderer: ArtifactRenderer<MarketResearchModel> = {
  type: 'market_research',
  matchNames: ['market_research'],
  parse: parseMarketResearch,
  toJson: marketToJson,
  toMarkdown: marketResearchToMarkdown,
  toHtml: marketResearchToHtml,
  Card: MarketResearchCard,
  Editor: MarketResearchEditor,
};
